import { PageIndices, TabIndices, SectionIndices, ModalIndices } from './i-doc.registry';

/**
 * getDocumentationTree() шукає PageIndex для роута евристично, зіставляючи
 * `route.path` з `/${slug}`, де slug — це лише другий сегмент id (після
 * "page:"). Для composite id ('page:expert:appointments' тощо) або коли
 * route.path має більше одного сегмента, евристика не спрацьовує і сторінка
 * потрапляє в дерево під синтетичним id замість реального PageIndex.
 * Тут — точкові виправлення для випадків, які реально зустрічаються.
 */
export const ROUTE_PAGE_OVERRIDES: Record<string, string> = {
  '/expert/appointments': PageIndices.EXPERT_APPOINTMENTS,
  '/appointments': PageIndices.CLIENT_APPOINTMENTS,
  '/expert/posts': PageIndices.EXPERT_POSTS,
  '/expert/prompts': PageIndices.EXPERT_PROMPTS,
  '/expert-posts': PageIndices.CLIENT_EXPERT_POSTS,
  '/reports': PageIndices.CLIENT_MY_REPORTS,
  '/admin/health-index': PageIndices.ADMIN_HEALTH_INDEX,
  '/pm/health-index': PageIndices.ADMIN_HEALTH_INDEX,
  '/admin/system-settings': PageIndices.ADMIN_SYSTEM_SETTINGS,
  '/admin/system-prompts': PageIndices.ADMIN_SYSTEM_PROMPTS,
  '/admin/finance': PageIndices.ADMIN_FINANCE,
  '/admin/expert-finance': PageIndices.ADMIN_EXPERT_FINANCE,
  '/admin/finance/clients': PageIndices.ADMIN_FINANCE_CLIENTS,
  '/admin/finance/experts': PageIndices.ADMIN_FINANCE_EXPERTS,
  '/admin/entitlements/clients': PageIndices.ADMIN_CLIENT_ENTITLEMENTS,
  '/admin/entitlements/experts': PageIndices.ADMIN_EXPERT_ENTITLEMENTS,
  '/admin/quiz-builder': PageIndices.ADMIN_QUIZ_BUILDER,
  '/admin/users/clients': PageIndices.ADMIN_CLIENTS,
  '/pm/users/clients': PageIndices.PM_CLIENTS,
  '/pm/users/experts': PageIndices.PM_EXPERTS,
  '/pm/users/all': PageIndices.PM_ALL_USERS,
  '/pm/legacy/clients': PageIndices.PM_LEGACY_CLIENTS,
  '/pm/legacy/experts': PageIndices.PM_LEGACY_EXPERTS,
  '/pm/legacy/orgs': PageIndices.PM_LEGACY_ORGS,
  '/pm/documentation': PageIndices.DOCUMENTATION,
  '/admin/users/experts': PageIndices.ADMIN_EXPERTS,
  '/admin/users/all': PageIndices.ADMIN_ALL_USERS,
  '/admin/devices': PageIndices.ADMIN_DEVICES,
  '/admin/foods': PageIndices.ADMIN_FOODS,
  '/admin/medications': PageIndices.ADMIN_MEDICATIONS,
  '/admin/workouts': PageIndices.ADMIN_WORKOUTS,
  '/admin/met-activities': PageIndices.ADMIN_MET_ACTIVITIES,
  '/admin/event-types': PageIndices.ADMIN_EVENT_TYPES,
  '/admin/extract_cache': PageIndices.ADMIN_AI_EXTRACT,
  '/admin/ai_reports': PageIndices.ADMIN_AI_REPORTS,
  '/admin/foods_cache': PageIndices.ADMIN_AI_FOOD,
  '/admin/workouts_cache': PageIndices.ADMIN_AI_WORKOUT,
  '/admin/scheduler': PageIndices.ADMIN_SCHEDULER,
  '/admin/news': PageIndices.ADMIN_NEWS,
  '/admin/notifications': PageIndices.ADMIN_NOTIFICATIONS,
  '/admin/telegram': PageIndices.ADMIN_TELEGRAM,
  '/admin/legacy-import': PageIndices.ADMIN_LEGACY_IMPORT,
  '/dev-push': PageIndices.DEV_PUSH,
  '/dev-ai-index': PageIndices.DEV_AI_INDEX,
  '/dev-native-iap': PageIndices.DEV_NATIVE_IAP,
  '/dev-payment-diagnostics': PageIndices.DEV_PAYMENT_DIAGNOSTICS,
  '/dev-finance-legacy': PageIndices.DEV_FINANCE_LEGACY,
};

/**
 * Вкладки, які автоматичний prefix-матчинг (`tab:{pageSlug}-*`) хибно
 * причепив би до цієї сторінки, хоча реально вони належать іншому екрану
 * без власного PageIndex (тут — /admin/notifications, а не /notifications).
 */
export const EXCLUDED_AUTO_TABS: Partial<Record<string, Set<string>>> = {
  [PageIndices.NOTIFICATIONS]: new Set([TabIndices.NOTIFICATIONS_LIST, TabIndices.NOTIFICATIONS_MAILING]),
};

/**
 * Ручна карта зв'язків для вузлів, які getDocumentationTree() не може вивести
 * автоматично з routeConfig (модалки, вкладені сторінки поза роутером, секції,
 * повторне використання одного tab-контенту в кількох місцях).
 *
 * Один вузол може мати кілька батьків — тоді він навмисно показується
 * в дереві документації в кожному з цих місць (той самий i-doc id — та сама
 * реальна модалка/сторінка в коді).
 *
 * Пілот: заповнено тільки для сайдбару ролі "Експерт".
 */
export const NODE_PARENTS: Record<string, string[]> = {
  // Клієнти
  [ModalIndices.CLIENT_DETAILS]: [TabIndices.CLIENTS_LIST],
  [ModalIndices.BULK_NOTIFICATION_CREATE]: [TabIndices.CLIENTS_BULK_NOTIFICATIONS],
  [ModalIndices.BULK_NOTIFICATION_DETAILS]: [TabIndices.CLIENTS_BULK_NOTIFICATIONS],

  // Клієнти -> картка клієнта (права підпанель ClientProfileSidebar)
  [PageIndices.EXPERT_CLIENT_DASHBOARD]: [PageIndices.CLIENTS],
  [PageIndices.EXPERT_CLIENT_PROFILE]: [PageIndices.CLIENTS],
  [PageIndices.EXPERT_CLIENT_CALENDAR]: [PageIndices.CLIENTS],
  [PageIndices.PM_CLIENT_DASHBOARD]: [PageIndices.PM_CLIENTS],
  [PageIndices.PM_CLIENT_PROFILE]: [PageIndices.PM_CLIENTS],
  [PageIndices.PM_CLIENT_CALENDAR]: [PageIndices.PM_CLIENTS],
  [PageIndices.PM_CLIENT_METRICS]: [PageIndices.PM_CLIENTS],
  [PageIndices.PM_CLIENT_DAILY_NORMS]: [PageIndices.PM_CLIENTS],
  [PageIndices.PM_EXPERT_DASHBOARD]: [PageIndices.PM_EXPERTS],
  [PageIndices.PM_EXPERT_CLIENTS]: [PageIndices.PM_EXPERTS],
  [PageIndices.EXPERT_CLIENT_QUESTIONNAIRES]: [PageIndices.CLIENTS],
  [PageIndices.EXPERT_CLIENT_FILES]: [PageIndices.CLIENTS],
  [PageIndices.EXPERT_CLIENT_REPORTS]: [PageIndices.CLIENTS],
  [PageIndices.EXPERT_CLIENT_APPOINTMENTS]: [PageIndices.CLIENTS],
  [PageIndices.EXPERT_CLIENT_NOTIFICATIONS]: [PageIndices.CLIENTS],
  [PageIndices.EXPERT_CLIENT_MAILINGS]: [PageIndices.CLIENTS],

  [PageIndices.EXPERT_CLIENT_QUESTIONNAIRE]: [PageIndices.EXPERT_CLIENT_QUESTIONNAIRES],

  [SectionIndices.EXPERT_CLIENT_DAILY_NORMS]: [PageIndices.EXPERT_CLIENT_DASHBOARD],
  [SectionIndices.EXPERT_CLIENT_PROFILE_HEADER]: [PageIndices.EXPERT_CLIENT_DASHBOARD],
  [SectionIndices.EXPERT_CLIENT_MAIN_GOAL]: [PageIndices.EXPERT_CLIENT_DASHBOARD],
  [SectionIndices.EXPERT_CLIENT_COLLABORATION_PLAN]: [PageIndices.EXPERT_CLIENT_DASHBOARD],
  [SectionIndices.EXPERT_CLIENT_RECOMMENDATIONS]: [PageIndices.EXPERT_CLIENT_DASHBOARD],
  [SectionIndices.EXPERT_CLIENT_METRICS_CARDS]: [PageIndices.EXPERT_CLIENT_DASHBOARD],
  [SectionIndices.CLIENT_EXPERT_NORMS]: [PageIndices.PM_CLIENT_DAILY_NORMS, ModalIndices.ADMIN_CLIENT_DETAIL],
  [ModalIndices.COLLABORATION_PLAN_ALL_TASKS]: [SectionIndices.EXPERT_CLIENT_COLLABORATION_PLAN],
  [ModalIndices.DAILY_NORM_EDIT]: [SectionIndices.EXPERT_CLIENT_DAILY_NORMS],
  [ModalIndices.DAILY_NORM_MACROS_EDIT]: [SectionIndices.EXPERT_CLIENT_DAILY_NORMS],
  [ModalIndices.METRIC_EXPLANATION]: [ModalIndices.DAILY_NORM_EDIT, ModalIndices.DAILY_NORM_MACROS_EDIT],
  [ModalIndices.CALCULATED_METRIC_INFO]: [
    PageIndices.CALCULATED_METRICS,
    PageIndices.PM_CLIENT_DAILY_NORMS,
    PageIndices.PM_CLIENT_METRICS,
  ],
  [TabIndices.METRICS_LATEST]: [PageIndices.PM_CLIENT_METRICS],
  [TabIndices.METRICS_HISTORY]: [PageIndices.PM_CLIENT_METRICS],
  [TabIndices.METRICS_CHARTS]: [PageIndices.PM_CLIENT_METRICS],
  [TabIndices.METRICS_METRICS]: [PageIndices.PM_CLIENT_METRICS],

  [TabIndices.CLIENT_REPORTS_ACTIVITY]: [PageIndices.EXPERT_CLIENT_REPORTS],
  [TabIndices.CLIENT_REPORTS_AI_HISTORY]: [PageIndices.EXPERT_CLIENT_REPORTS],
  [ModalIndices.CLIENT_AI_REPORT_DETAIL]: [PageIndices.EXPERT_CLIENT_REPORTS],
  [ModalIndices.AI_ANALYSIS]: [PageIndices.EXPERT_CLIENT_REPORTS],

  [ModalIndices.CALENDAR_EVENT_DETAIL]: [PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.PM_CALENDAR_EVENT]: [PageIndices.PM_CLIENT_CALENDAR],
  [ModalIndices.ADD_CLIENT_EVENT]: [PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.FOOD_EVENT_DETAIL]: [PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.QUICK_FOOD_ADD]: [PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.QUICK_WORKOUT_ADD]: [PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.QUICK_MEDICATION_ADD]: [PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.QUICK_WATER_ADD]: [PageIndices.EXPERT_CLIENT_CALENDAR],

  // Календар (особистий) + Клієнти -> Календар клієнта: та сама подієва модалка в обох місцях
  [ModalIndices.ADD_FOOD_EVENT]: [TabIndices.CALENDAR_MY_CALENDAR, PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.ADD_FEELING_EVENT]: [TabIndices.CALENDAR_MY_CALENDAR, PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.ADD_TASK_EVENT]: [TabIndices.CALENDAR_MY_CALENDAR, PageIndices.EXPERT_CLIENT_CALENDAR],
  [ModalIndices.ADD_WORKOUT_EVENT]: [TabIndices.CALENDAR_MY_CALENDAR],
  [ModalIndices.ADD_MEDICATION_EVENT]: [TabIndices.CALENDAR_MY_CALENDAR],
  [ModalIndices.ADD_LIQUID_EVENT]: [TabIndices.CALENDAR_MY_CALENDAR],
  [ModalIndices.EVENT_EDIT]: [TabIndices.CALENDAR_MY_CALENDAR],
  [ModalIndices.SCHEDULE_SLOT_EDIT]: [TabIndices.CALENDAR_SLOTS],

  // Зустрічі
  [SectionIndices.EXPERT_APPOINTMENTS_LIST]: [TabIndices.EXPERT_APPOINTMENTS_LIST],
  [ModalIndices.EXPERT_CREATE_APPOINTMENT]: [SectionIndices.EXPERT_APPOINTMENTS_LIST],
  [ModalIndices.EXPERT_APPOINTMENT_DETAILS]: [SectionIndices.EXPERT_APPOINTMENTS_LIST],

  // Конструктор опитувань
  [ModalIndices.QUIZ_BUILDER_TEMPLATE_FORM]: [PageIndices.QUIZ_BUILDER],
  [PageIndices.QUIZ_BUILDER_EDITOR]: [PageIndices.QUIZ_BUILDER],
  [ModalIndices.QUIZ_BUILDER_STEP_FORM]: [PageIndices.QUIZ_BUILDER_EDITOR],
  [ModalIndices.QUIZ_BUILDER_QUESTION_FORM]: [PageIndices.QUIZ_BUILDER_EDITOR],
  [ModalIndices.QUIZ_BUILDER_OPTION_FORM]: [PageIndices.QUIZ_BUILDER_EDITOR],

  // Мої пости / Бібліотека промптів
  [ModalIndices.EXPERT_POST_FORM]: [PageIndices.EXPERT_POSTS],
  [ModalIndices.EXPERT_PROMPT_DETAIL]: [PageIndices.EXPERT_PROMPTS],
  [ModalIndices.EXPERT_PROMPT_FORM]: [PageIndices.EXPERT_PROMPTS],

  // EHI - одна сторінка /admin/health-index (2026-09-22: журнал /admin/ehi-journal злито з калькулятором).
  // Вкладки журналу мають id `tab:admin-ehi-*`, а не `tab:admin-health-index-*`, тож автопідбір вкладок за
  // префіксом id сторінки їх не бачить - прив'язано тут. Id не перейменовано, щоб не ламати data-i-doc/PageHint.
  [TabIndices.ADMIN_EHI_SUMMARIES]: [PageIndices.ADMIN_HEALTH_INDEX],
  [TabIndices.ADMIN_EHI_SNAPSHOTS]: [PageIndices.ADMIN_HEALTH_INDEX],
  [ModalIndices.ADMIN_HEALTH_INDEX_RESULT]: [PageIndices.ADMIN_HEALTH_INDEX],
  [ModalIndices.ADMIN_EHI_REFRESH_RESULT]: [TabIndices.ADMIN_EHI_SNAPSHOTS],
  [ModalIndices.ADMIN_EHI_SNAPSHOT_DETAILS]: [TabIndices.ADMIN_EHI_SNAPSHOTS],
  [ModalIndices.ADMIN_EHI_SUMMARY_RECALC_RESULT]: [TabIndices.ADMIN_EHI_SUMMARIES],
  [ModalIndices.ADMIN_EHI_USER_REFRESH_RESULT]: [TabIndices.ADMIN_EHI_SUMMARIES],
  [ModalIndices.ADMIN_EHI_SUMMARY_DETAILS]: [TabIndices.ADMIN_EHI_SUMMARIES],

  // Налаштування
  [ModalIndices.EXPERT_PAYMENT]: [TabIndices.SETTINGS_EXPERT_SUBSCRIPTION],

  // Сповіщення
  [ModalIndices.NOTIFICATION_DETAIL]: [PageIndices.NOTIFICATIONS],
};

/**
 * Людські підписи для вузлів, яких немає в routeConfig (модалки, вкладені
 * сторінки, секції) — для них getDocumentationTree() не може взяти назву
 * з роута, тож підпис задається тут вручну.
 */
export const NODE_LABELS: Record<string, string> = {
  [ModalIndices.CLIENT_DETAILS]: 'Картка клієнта',
  [ModalIndices.BULK_NOTIFICATION_CREATE]: 'Нова розсилка',
  [ModalIndices.BULK_NOTIFICATION_DETAILS]: 'Деталі розсилки',

  [PageIndices.EXPERT_CLIENT_DASHBOARD]: 'Дашборд клієнта',
  [PageIndices.EXPERT_CLIENT_PROFILE]: 'Профіль клієнта',
  [PageIndices.EXPERT_CLIENT_CALENDAR]: 'Календар клієнта',
  [PageIndices.PM_CLIENT_DAILY_NORMS]: 'Денні норми клієнта',
  [PageIndices.PM_CLIENT_PROFILE]: 'Профіль клієнта',
  [PageIndices.PM_CLIENT_METRICS]: 'Метрики клієнта',
  [PageIndices.EXPERT_CLIENT_QUESTIONNAIRES]: 'Опитувальники клієнта',
  [PageIndices.EXPERT_CLIENT_QUESTIONNAIRE]: 'Проходження опитувальника',
  [PageIndices.EXPERT_CLIENT_FILES]: 'Файли клієнта',
  [PageIndices.EXPERT_CLIENT_REPORTS]: 'Звіти клієнта',
  [PageIndices.EXPERT_CLIENT_APPOINTMENTS]: 'Зустрічі з клієнтом',
  [PageIndices.EXPERT_CLIENT_NOTIFICATIONS]: 'Сповіщення клієнту',
  [PageIndices.EXPERT_CLIENT_MAILINGS]: 'Розсилки клієнту',

  [ModalIndices.COLLABORATION_PLAN_ALL_TASKS]: 'План співпраці - всі задачі',
  [ModalIndices.CALCULATED_METRIC_INFO]: 'Пояснення показника',
  [ModalIndices.DAILY_NORM_EDIT]: 'Редагування денної норми',
  [ModalIndices.DAILY_NORM_MACROS_EDIT]: 'Редагування норм БЖВ',
  [ModalIndices.METRIC_EXPLANATION]: 'Пояснення розрахунку метрики для клієнта',
  [ModalIndices.CLIENT_AI_REPORT_DETAIL]: 'AI-звіт (деталі)',
  [ModalIndices.AI_ANALYSIS]: 'AI-аналіз',

  [ModalIndices.CALENDAR_EVENT_DETAIL]: 'Деталі події',
  [ModalIndices.PM_CALENDAR_EVENT]: 'Подія календаря (продакт)',
  [ModalIndices.ADD_CLIENT_EVENT]: 'Додати подію клієнту',
  [ModalIndices.FOOD_EVENT_DETAIL]: 'Деталі прийому їжі',
  [ModalIndices.QUICK_FOOD_ADD]: 'Швидко додати їжу',
  [ModalIndices.QUICK_WORKOUT_ADD]: 'Швидко додати тренування',
  [ModalIndices.QUICK_MEDICATION_ADD]: 'Швидко додати медикамент',
  [ModalIndices.QUICK_WATER_ADD]: 'Швидко додати воду',
  [ModalIndices.ADD_FOOD_EVENT]: 'Додати прийом їжі',
  [ModalIndices.ADD_FEELING_EVENT]: 'Додати самопочуття',
  [ModalIndices.ADD_TASK_EVENT]: 'Додати задачу',
  [ModalIndices.ADD_WORKOUT_EVENT]: 'Додати тренування',
  [ModalIndices.ADD_MEDICATION_EVENT]: 'Додати медикамент',
  [ModalIndices.ADD_LIQUID_EVENT]: 'Додати рідину',
  [ModalIndices.EVENT_EDIT]: 'Редагувати подію',
  [ModalIndices.SCHEDULE_SLOT_EDIT]: 'Редагувати слот',

  [SectionIndices.EXPERT_APPOINTMENTS_LIST]: 'Таблиця записів',
  [SectionIndices.EXPERT_CLIENT_DAILY_NORMS]: 'Денні норми клієнта',
  [SectionIndices.EXPERT_CLIENT_PROFILE_HEADER]: 'Шапка профілю клієнта',
  [SectionIndices.EXPERT_CLIENT_MAIN_GOAL]: 'Головна ціль клієнта',
  [SectionIndices.EXPERT_CLIENT_COLLABORATION_PLAN]: 'План співпраці',
  [SectionIndices.EXPERT_CLIENT_RECOMMENDATIONS]: 'Виконання рекомендацій',
  [SectionIndices.EXPERT_CLIENT_METRICS_CARDS]: 'Метрики клієнта (дашборд)',
  [SectionIndices.CLIENT_EXPERT_NORMS]: 'Норми експертів для клієнта',
  [ModalIndices.EXPERT_CREATE_APPOINTMENT]: 'Створити запис клієнту',
  [ModalIndices.EXPERT_APPOINTMENT_DETAILS]: 'Деталі запису',

  [ModalIndices.QUIZ_BUILDER_TEMPLATE_FORM]: 'Форма шаблону',
  [PageIndices.QUIZ_BUILDER_EDITOR]: 'Редактор опитувальника',
  [ModalIndices.QUIZ_BUILDER_STEP_FORM]: 'Форма кроку',
  [ModalIndices.QUIZ_BUILDER_QUESTION_FORM]: 'Форма питання',
  [ModalIndices.QUIZ_BUILDER_OPTION_FORM]: 'Форма варіанту відповіді',

  [ModalIndices.EXPERT_POST_FORM]: 'Форма поста',
  [ModalIndices.EXPERT_PROMPT_DETAIL]: 'Перегляд промпту',
  [ModalIndices.EXPERT_PROMPT_FORM]: 'Форма промпту',

  [TabIndices.ADMIN_EHI_SUMMARIES]: 'Узагальнені',
  [TabIndices.ADMIN_EHI_SNAPSHOTS]: 'Знімки',
  [TabIndices.ADMIN_SYSTEM_SETTINGS_GENERAL]: 'Загальні',
  [TabIndices.ADMIN_NOTIFICATIONS_CATALOG]: 'Каталог сповіщень',
  [ModalIndices.ADMIN_HEALTH_INDEX_RESULT]: 'Результат розрахунку',
  [ModalIndices.ADMIN_EHI_REFRESH_RESULT]: 'Підсумок оновлення EHI',
  [ModalIndices.ADMIN_EHI_SUMMARY_RECALC_RESULT]: 'Підсумок перерахунку узагальнених даних',
  [ModalIndices.ADMIN_EHI_USER_REFRESH_RESULT]: 'Підсумок комплексного оновлення EHI користувача',
  [ModalIndices.ADMIN_EHI_SNAPSHOT_DETAILS]: 'Деталі знімка EHI',
  [ModalIndices.ADMIN_EHI_SUMMARY_DETAILS]: 'Деталі розрахунку EHI користувача',
  [ModalIndices.EXPERT_PAYMENT]: 'Оплата підписки',
  [ModalIndices.NOTIFICATION_DETAIL]: 'Деталі сповіщення',
};
